import { isDeepEqual } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import _uniqWith from 'lodash-es/uniqWith'

import { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateTuple } from './create-tuple'
import { UnsupportedNodeError } from './utils'

/**
 * Returns the unique items in the given list. I.e., deduplicates the items in
 * the list
 *
 * @param expression Expression that returns a list
 * @example
 * { uniq: { '[]': [ 1, 2, 1, 3, 3, 1, 4 ] } }
 * // ->
 * [ 1, 2, 3, 4 ]
 */
export interface Unique<out T = any> {
  /** {@link Unique} */
  readonly [Key.Unique]: T extends readonly any[] ? Expression<T> : never
}
export namespace Unique {
  export const isUnique: TypeChecker<Unique> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Unique>({
      [Key.Unique]: Expression.isExpression,
    }),
  ).usingAlias('Fn.Unique')
  export const assertIsUnique: TypeAsserter<Unique> =
    TypeAsserter.Factory.from(isUnique)

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends readonly any[] = readonly unknown[],
  > extends fnNode.Node<T, Key.Unique, fnNode.Node> {
    public get type() {
      return this.arg.type
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Unique, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<Unique> = {
    createNode: (fn, context) => {
      const arg = expressionNode.from(fn[Key.Unique], context)
      if (expressionNode.ForValue.isForValue(arg)) {
        // If we have a value node, the count is the length of the array. If the
        // value is not an array (whether it is nullish or just mistyped), we
        // return empty list
        return new expressionNode.ForValue(
          TypeChecker.isReadonlyArray(arg.value)
            ? _uniqWith(arg.value, isDeepEqual)
            : [],
        )
      } else if (arg instanceof CreateTuple.Node) {
        // If this is a tuple, we can deduplicate any value nodes in the tuple
        const values: unknown[] = []
        const uniqueNodes = arg.arg.filter(node => {
          if (!expressionNode.ForValue.isForValue(node)) {
            // If this is not a value node, we cannot deduplicate it
            return true
          }
          if (values.some(value => isDeepEqual(value, node.value))) {
            // If it is a value node, and its value is equal to one we have
            // already seen, we remove it from the tuple
            return false
          }
          // Otherwise, we store the value to compare to future tuple elements
          // and continue on
          values.push(node.value)
          return true
        })
        return new Node(
          // If we were able to deduplicate any nodes in the tuple, we create a
          // new tuple
          uniqueNodes.length < arg.arg.length
            ? new CreateTuple.Node(uniqueNodes)
            : arg,
        )
      } else if (arg instanceof fnNode.Node) {
        return new Node(arg satisfies fnNode.Node as fnNode.Node)
      } else {
        throw new UnsupportedNodeError()
      }
    },
    expressionsInArgs: fn => [fn[Key.Unique]],
    typeChecker: isUnique,
  }
}
